import type { HomePlanStatus } from "api-types/homePlans/homePlanTypes";
import {
  locationSummaryCategoryOrder,
  type ClientLocationHomePlansSummary,
} from "api-types/locations/locationsResponses";
import { useMemo, useState, type ReactNode } from "react";
import ToggleGroup from "ui/forms/ToggleGroup";
import lazySuspense from "ui/hooks/lazySuspense";
import IconPhaseConstruction from "ui/icons/IconPhaseConstruction";
import IconPhasePlanPrep from "ui/icons/IconPhasePlanPrep";
import IconPhasePreConstruction from "ui/icons/IconPhasePreConstruction";
import { formatBreakdownValue } from "./locationSummaryFormatting";
import { locationSummaryCategoryLabels, locationSummaryPhaseLabels } from "./locationSummaryLabels";
import type { BreakdownView } from "./locationSummaryTypes";
import styles from "./LocationSummaryView.module.scss";

const DonutChart = lazySuspense(() => import("./DonutChart"));

const phaseColorVars: Record<HomePlanStatus, string> = {
  PREP: "--location-summary-phase-prep-color",
  PRE_CONSTRUCTION: "--location-summary-phase-pre-construction-color",
  CONSTRUCTION: "--location-summary-phase-construction-color",
};

const phaseIcons: Record<HomePlanStatus, ReactNode> = {
  PREP: <IconPhasePlanPrep size={16} />,
  PRE_CONSTRUCTION: <IconPhasePreConstruction size={16} />,
  CONSTRUCTION: <IconPhaseConstruction size={16} />,
};

function isPhaseKey(key: HomePlanStatus | "TOTAL"): key is HomePlanStatus {
  return key !== "TOTAL";
}

export default function HomePlanDetailsSection({ summary }: { summary: ClientLocationHomePlansSummary }): ReactNode {
  const [view, setView] = useState<BreakdownView>("count");
  // A fresh array each render would re-run DonutChart's color effect and drop any live hover.
  const phaseSegments = useMemo(
    () =>
      summary.phaseRows.flatMap((row) =>
        isPhaseKey(row.key)
          ? [{ label: locationSummaryPhaseLabels[row.key], value: row.total, colorVar: phaseColorVars[row.key] }]
          : [],
      ),
    [summary.phaseRows],
  );

  return (
    <section className={styles.card} data-testid="home-plan-details">
      <div className={styles.cardHeaderRow}>
        <div className={styles.cardHeader}>
          <h3>Home Plan Details</h3>
          <p>Number of homes in each phase broken down by customer status.</p>
        </div>
        <ToggleGroup.Root
          type="single"
          value={view}
          onValueChange={(value: string) => {
            if (value) {
              setView(value as BreakdownView);
            }
          }}
          allowEmpty={false}
          toggleStyle="secondary"
          className={styles.countToggle}
          aria-label="Home plan details display"
        >
          <ToggleGroup.Item value="count" aria-label="Show counts">
            #
          </ToggleGroup.Item>
          <ToggleGroup.Item value="percentage" aria-label="Show percentages">
            %
          </ToggleGroup.Item>
        </ToggleGroup.Root>
      </div>
      <div className={styles.detailsBody}>
        <DonutChart segments={phaseSegments} ariaLabel="Homes by phase" size={96} showTooltip />
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Phase</th>
                {locationSummaryCategoryOrder.map((category) => (
                  <th scope="col" key={category}>
                    {locationSummaryCategoryLabels[category]}
                  </th>
                ))}
                <th scope="col">{locationSummaryCategoryLabels.TOTAL}</th>
              </tr>
            </thead>
            <tbody>
              {summary.phaseRows.map((row) => (
                <tr key={row.key} className={row.key === "TOTAL" ? styles.totalRow : undefined}>
                  <th scope="row">
                    {isPhaseKey(row.key) ? (
                      <span className={styles.phaseLabel}>
                        <span
                          className={styles.legendSwatch}
                          style={{ backgroundColor: `var(${phaseColorVars[row.key]})` }}
                          aria-hidden
                        />
                        {phaseIcons[row.key]}
                        {locationSummaryPhaseLabels[row.key]}
                      </span>
                    ) : (
                      locationSummaryPhaseLabels[row.key]
                    )}
                  </th>
                  {locationSummaryCategoryOrder.map((category) => (
                    <td key={category} data-zero={row.counts[category] === 0 || undefined}>
                      {formatBreakdownValue(row.counts[category], summary.totalHomes, view)}
                    </td>
                  ))}
                  <td data-zero={row.total === 0 || undefined}>
                    {formatBreakdownValue(row.total, summary.totalHomes, view)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
